import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import GameCanvas from './GameCanvas';
import HUD from './HUD';
import DraftModal from './DraftModal';
import DraftNotification from './DraftNotification';
import MobileControls from './MobileControls';
import { generateMaze } from '../utils/mazeGenerator';
import { GAME_CONFIG, PLAYER_CONFIG, ENEMY_CONFIG, ABILITY_CONFIG, getLevelById, FEAR_MESSAGES, UI_TEXT } from '../config/gameConfig';
import { draftTextManager } from '../utils/draftTextManager';
import { audioManager } from '../utils/audioManager';
import { useDeviceDetection } from '../hooks/useDeviceDetection';
import { usePreventScroll } from '../hooks/usePreventScroll';

const GameScreen = ({ level, onNextLevel, onGameOver, collectedDrafts, setCollectedDrafts }) => {
  const { isMobile } = useDeviceDetection();
  usePreventScroll(isMobile);

  const [maze, setMaze] = useState(null);
  const [playerPos, setPlayerPos] = useState({ x: PLAYER_CONFIG.startX, y: PLAYER_CONFIG.startY });
  const [fears, setFears] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [portal, setPortal] = useState(null);
  const [gameTime, setGameTime] = useState(0);
  const [showPortalHint, setShowPortalHint] = useState(false);
  const [abilityCooldown, setAbilityCooldown] = useState(0);
  const [isAbilityActive, setIsAbilityActive] = useState(false);
  const [joystickDirection, setJoystickDirection] = useState({ x: 0, y: 0 });
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [currentDraftText, setCurrentDraftText] = useState('');
  const [collectedDraftsList, setCollectedDraftsList] = useState([]);
  const [notificationQueue, setNotificationQueue] = useState([]);
  const [isShowingNotification, setIsShowingNotification] = useState(false);

  const gameLoopRef = useRef(null);
  const lastTimeRef = useRef(0);
  const keysPressed = useRef({});

  const MAZE_SIZE = GAME_CONFIG.mazeSize;

  const calculateCellSize = () => {
    if (!isMobile) return 45;
    const w = window.innerWidth;
    const h = window.innerHeight;
    const widthConstraint = w * 0.85;
    const heightConstraint = h - 300;
    const sizeBasedOnWidth = Math.floor(widthConstraint / MAZE_SIZE);
    const sizeBasedOnHeight = Math.floor(heightConstraint / MAZE_SIZE);
    return Math.min(sizeBasedOnWidth, sizeBasedOnHeight);
  };

  const CELL_SIZE = calculateCellSize();
  const PLAYER_SPEED = isMobile ? PLAYER_CONFIG.speedMobile : PLAYER_CONFIG.speedDesktop;
  const PLAYER_RADIUS = PLAYER_CONFIG.radius;
  const ABILITY_DURATION = isMobile ? ABILITY_CONFIG.durationMobile : ABILITY_CONFIG.durationDesktop;
  const ABILITY_COOLDOWN = isMobile ? ABILITY_CONFIG.cooldownMobile : ABILITY_CONFIG.cooldownDesktop;

  const levelData = getLevelById(level);

  // Handle notification queue
  useEffect(() => {
    if (notificationQueue.length > 0 && !isShowingNotification) {
      setIsShowingNotification(true);
      const currentNotification = notificationQueue[0];
      setCurrentDraftText(currentNotification);

      setTimeout(() => {
        setIsShowingNotification(false);
        setNotificationQueue(prev => prev.slice(1));
      }, 3000);
    }
  }, [notificationQueue, isShowingNotification]);

  useEffect(() => {
    initializeLevel();
    audioManager.init();

    if (levelData && levelData.musicSources) {
      audioManager.playMusic(levelData.musicSources);
    }
  }, [level]);

  useEffect(() => {
    if (isMobile) return;

    const handleKeyDown = (e) => {
      const key = e.key.toLowerCase();
      keysPressed.current[key] = true;

      if (key === ABILITY_CONFIG.activationKey && abilityCooldown <= 0 && !isAbilityActive) {
        triggerAbility();
      }
    };

    const handleKeyUp = (e) => {
      keysPressed.current[e.key.toLowerCase()] = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [abilityCooldown, isAbilityActive, isMobile]);

  const handleDirectionChange = (direction) => {
    setJoystickDirection(direction);
  };

  const triggerAbility = () => {
    setIsAbilityActive(true);
    setAbilityCooldown(ABILITY_COOLDOWN);
    audioManager.playAbility();

    setFears(prev => prev.map(f => ({ ...f, isFrozen: true })));

    setTimeout(() => {
      setIsAbilityActive(false);
      setFears(prev => prev.map(f => ({ ...f, isFrozen: false })));
    }, ABILITY_DURATION);
  };

  const initializeLevel = () => {
    const newMaze = generateMaze(MAZE_SIZE, MAZE_SIZE);
    setMaze(newMaze);

    const validPositions = [];
    for (let y = 0; y < MAZE_SIZE; y++) {
      for (let x = 0; x < MAZE_SIZE; x++) {
        if (newMaze[y][x] === 0) {
          validPositions.push({ x: x + 0.5, y: y + 0.5 });
        }
      }
    }

    setPlayerPos({ x: PLAYER_CONFIG.startX, y: PLAYER_CONFIG.startY });

    const newDrafts = [];
    const availableForDrafts = validPositions.filter(p =>
      Math.abs(p.x - PLAYER_CONFIG.startX) + Math.abs(p.y - PLAYER_CONFIG.startY) > 3
    );

    for (let i = 0; i < GAME_CONFIG.draftsPerLevel; i++) {
      if (availableForDrafts.length === 0) break;
      const idx = Math.floor(Math.random() * availableForDrafts.length);
      const pos = availableForDrafts[idx];
      availableForDrafts.splice(idx, 1);
      newDrafts.push({
        id: i,
        x: pos.x,
        y: pos.y,
        text: draftTextManager.getRandomText()
      });
    }

    setDrafts(newDrafts);

    const fearCount = isMobile
      ? ENEMY_CONFIG.countMobile
      : Math.min(
          ENEMY_CONFIG.baseCountDesktop + Math.floor(level / 2),
          ENEMY_CONFIG.maxCountDesktop
        );

    const newFears = [];
    const availableForFears = validPositions.filter(p =>
      Math.abs(p.x - PLAYER_CONFIG.startX) + Math.abs(p.y - PLAYER_CONFIG.startY) > ENEMY_CONFIG.minSpawnDistance
    );

    const baseSpeed = isMobile
      ? ENEMY_CONFIG.speedMobile
      : ENEMY_CONFIG.baseSpeed + (level * ENEMY_CONFIG.speedPerLevel);

    for (let i = 0; i < fearCount; i++) {
      if (availableForFears.length === 0) break;
      const idx = Math.floor(Math.random() * availableForFears.length);
      const pos = availableForFears[idx];
      availableForFears.splice(idx, 1);
      newFears.push({
        id: i,
        x: pos.x,
        y: pos.y,
        type: levelData.fearType,
        state: 'idle',
        timer: 0,
        speed: baseSpeed,
        isFrozen: false
      });
    }

    setFears(newFears);
    setGameTime(0);
    setCollectedDrafts(0);
    setCollectedDraftsList([]);
    setPortal(null);
    setShowPortalHint(false);
    setAbilityCooldown(0);
    setIsAbilityActive(false);
    setShowDraftModal(false);
    setCurrentDraftText('');
    setJoystickDirection({ x: 0, y: 0 });
    setNotificationQueue([]);
    setIsShowingNotification(false);
  };

  const checkWallCollision = (x, y) => {
    if (!maze) return true;

    const corners = [
      { x: x - PLAYER_RADIUS, y: y - PLAYER_RADIUS },
      { x: x + PLAYER_RADIUS, y: y - PLAYER_RADIUS },
      { x: x - PLAYER_RADIUS, y: y + PLAYER_RADIUS },
      { x: x + PLAYER_RADIUS, y: y + PLAYER_RADIUS }
    ];

    for (const p of corners) {
      const gx = Math.floor(p.x);
      const gy = Math.floor(p.y);
      if (gx < 0 || gx >= MAZE_SIZE || gy < 0 || gy >= MAZE_SIZE || maze[gy][gx] === 1) {
        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    const gameLoop = (timestamp) => {
      if (lastTimeRef.current === 0) lastTimeRef.current = timestamp;
      const deltaTime = Math.min(timestamp - lastTimeRef.current, 50);
      lastTimeRef.current = timestamp;

      if (showDraftModal) {
        gameLoopRef.current = requestAnimationFrame(gameLoop);
        return;
      }

      setGameTime(prev => prev + deltaTime);

      if (abilityCooldown > 0) {
        setAbilityCooldown(prev => Math.max(0, prev - deltaTime));
      }

      updatePlayer(deltaTime);
      updateFears(deltaTime);

      gameLoopRef.current = requestAnimationFrame(gameLoop);
    };

    gameLoopRef.current = requestAnimationFrame(gameLoop);

    return () => {
      if (gameLoopRef.current) cancelAnimationFrame(gameLoopRef.current);
    };
  }, [maze, drafts, fears, portal, abilityCooldown, joystickDirection, isMobile, showDraftModal]);

  const updatePlayer = (deltaTime) => {
    if (!maze) return;

    let dx = 0;
    let dy = 0;

    if (isMobile) {
      dx = joystickDirection.x;
      dy = joystickDirection.y;
    } else {
      const keys = keysPressed.current;
      if (keys['w'] || keys['arrowup']) dy -= 1;
      if (keys['s'] || keys['arrowdown']) dy += 1;
      if (keys['a'] || keys['arrowleft']) dx -= 1;
      if (keys['d'] || keys['arrowright']) dx += 1;
    }

    if (dx !== 0 && dy !== 0) {
      const length = Math.sqrt(dx * dx + dy * dy);
      dx /= length;
      dy /= length;
    }

    if ((dx !== 0 || dy !== 0) && Math.random() < 0.05) {
      audioManager.playStep();
    }

    if (dx === 0 && dy === 0) return;

    setPlayerPos(prev => {
      const moveDist = PLAYER_SPEED * (deltaTime / 1000);
      let newX = prev.x + dx * moveDist;
      let newY = prev.y;

      if (checkWallCollision(newX, newY)) newX = prev.x;

      newY = prev.y + dy * moveDist;
      if (checkWallCollision(newX, newY)) newY = prev.y;

      const collected = drafts.find(d =>
        Math.abs(d.x - newX) < 0.6 && Math.abs(d.y - newY) < 0.6
      );

      if (collected) {
        handleCollectDraft(collected);
      }

      if (portal) {
        const distToPortal = Math.sqrt(
          Math.pow(portal.x - newX, 2) + Math.pow(portal.y - newY, 2)
        );
        if (distToPortal < 0.7) {
          handleEnterPortal();
        }
      }

      return { x: newX, y: newY };
    });
  };

  const handleCollectDraft = (draft) => {
    audioManager.playCollect();
    setDrafts(prev => prev.filter(d => d.id !== draft.id));
    setCollectedDrafts(prev => prev + 1);
    setCollectedDraftsList(prev => [...prev, draft.text]);

    if (isMobile) {
      setNotificationQueue(prev => [...prev, draft.text]);
    }

    if (drafts.length === 1) {
      setTimeout(() => {
        spawnPortal();
      }, 500);
    }
  };

  const spawnPortal = () => {
    if (!maze) return;

    audioManager.playSpawnDoor();

    const validPositions = [];
    for (let y = 0; y < MAZE_SIZE; y++) {
      for (let x = 0; x < MAZE_SIZE; x++) {
        if (maze[y][x] === 0) {
          const dist = Math.abs(x + 0.5 - playerPos.x) + Math.abs(y + 0.5 - playerPos.y);
          if (dist > 8) {
            validPositions.push({ x: x + 0.5, y: y + 0.5 });
          }
        }
      }
    }

    if (validPositions.length > 0) {
      const pos = validPositions[Math.floor(Math.random() * validPositions.length)];
      setPortal(pos);
      setShowPortalHint(true);
      setTimeout(() => setShowPortalHint(false), 4000);
    }
  };

  const handleEnterPortal = () => {
    audioManager.playDoorEnter();
    const score = Math.max(1000 - Math.floor(gameTime / 100), 100);
    onNextLevel(score);
  };

  const updateFears = (deltaTime) => {
    if (!maze) return;

    setFears(prevFears =>
      prevFears.map(fear => {
        if (fear.isFrozen) return fear;

        const distToPlayer = Math.sqrt(
          Math.pow(playerPos.x - fear.x, 2) + Math.pow(playerPos.y - fear.y, 2)
        );

        if (distToPlayer < ENEMY_CONFIG.collisionDistance) {
          audioManager.playDamage();
          audioManager.stopMusic();
          
          // Random fear message selection
          const randomFearMessage = FEAR_MESSAGES[Math.floor(Math.random() * FEAR_MESSAGES.length)];
          onGameOver(randomFearMessage);
        }

        let dx = 0;
        let dy = 0;
        const speed = fear.speed * (deltaTime / 1000);

        switch (fear.type) {
          case 'doubt':
            if (!fear.targetX || Math.random() < 0.02) {
              const angle = Math.random() * Math.PI * 2;
              fear.vx = Math.cos(angle);
              fear.vy = Math.sin(angle);
              fear.targetX = true;
            }
            dx = fear.vx;
            dy = fear.vy;
            break;

          case 'uncertainty':
            if (distToPlayer < 4) {
              dx = playerPos.x - fear.x;
              dy = playerPos.y - fear.y;
            } else {
              if (!fear.vx || Math.random() < 0.01) {
                const angle = Math.random() * Math.PI * 2;
                fear.vx = Math.cos(angle);
                fear.vy = Math.sin(angle);
              }
              dx = fear.vx;
              dy = fear.vy;
            }
            break;

          case 'vulnerability':
            dx = playerPos.x - fear.x;
            dy = playerPos.y - fear.y;
            break;

          case 'judgment':
            fear.timer = (fear.timer || 0) + deltaTime;
            if (fear.timer < 1000) {
              dx = 0;
              dy = 0;
            } else if (fear.timer < 2500) {
              dx = playerPos.x - fear.x;
              dy = playerPos.y - fear.y;
            } else {
              fear.timer = 0;
            }
            break;

          case 'burnout':
            dx = playerPos.x - fear.x;
            dy = playerPos.y - fear.y;
            break;

          default:
            dx = 0;
            dy = 0;
        }

        if (dx !== 0 || dy !== 0) {
          const len = Math.sqrt(dx * dx + dy * dy);
          if (len > 0) {
            dx /= len;
            dy /= len;
          }
        }

        let newX = fear.x + dx * speed;
        let newY = fear.y;

        const checkEnemyWall = (ex, ey) => {
          const gx = Math.floor(ex);
          const gy = Math.floor(ey);
          return (gx < 0 || gx >= MAZE_SIZE || gy < 0 || gy >= MAZE_SIZE || maze[gy][gx] === 1);
        };

        if (checkEnemyWall(newX, newY)) newX = fear.x;

        newY = fear.y + dy * speed;
        if (checkEnemyWall(newX, newY)) newY = fear.y;

        return { ...fear, x: newX, y: newY };
      })
    );
  };

  if (!maze || !levelData) return null;

  return (
    <div
      className={`flex flex-col relative overflow-hidden ${isMobile ? 'h-screen fixed inset-0' : 'min-h-screen items-center justify-center'}`}
      style={{
        backgroundColor: levelData.bgColor,
        touchAction: 'none',
        paddingTop: isMobile ? '8px' : '0',
        paddingBottom: isMobile ? '8px' : '0'
      }}
    >
      <LevelEffects level={level} />

      <HUD
        level={level}
        levelName={levelData.name}
        collectedDrafts={collectedDrafts}
        totalDrafts={GAME_CONFIG.draftsPerLevel}
        time={Math.floor(gameTime / 1000)}
        abilityCooldown={abilityCooldown}
        isAbilityActive={isAbilityActive}
        isMobile={isMobile}
      />

      {!isMobile && (
        <>
          <div className="absolute inset-y-0 left-0 w-64 p-8 flex flex-col justify-center pointer-events-none hidden xl:flex">
            {collectedDraftsList.filter((_, i) => i % 2 === 0).map((text, i) => (
              <motion.div
                key={`left-${i}`}
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 0.4 }}
                className="mb-6 text-white/80 font-serif italic text-sm border-l-2 border-cyan-500/50 pl-4"
              >
                "{text}"
              </motion.div>
            ))}
          </div>

          <div className="absolute inset-y-0 right-0 w-64 p-8 flex flex-col justify-center pointer-events-none hidden xl:flex text-right">
            {collectedDraftsList.filter((_, i) => i % 2 !== 0).map((text, i) => (
              <motion.div
                key={`right-${i}`}
                initial={{ x: 50, opacity: 0 }}
                animate={{ x: 0, opacity: 0.4 }}
                className="mb-6 text-white/80 font-serif italic text-sm border-r-2 border-cyan-500/50 pr-4"
              >
                "{text}"
              </motion.div>
            ))}
          </div>
        </>
      )}

      <div
        className={`relative z-0 ${isMobile ? 'flex-1 flex items-center justify-center w-full' : ''}`}
        style={isMobile ? { paddingBottom: '160px', paddingTop: '80px' } : {}}
      >
        <GameCanvas
          maze={maze}
          playerPos={playerPos}
          fears={fears}
          drafts={drafts}
          portal={portal}
          cellSize={CELL_SIZE}
          levelTheme={levelData}
        />

        {isAbilityActive && (
          <>
            <motion.div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-4 border-cyan-400"
              style={{ width: '100px', height: '100px' }}
              animate={{ scale: [1, 10], opacity: [1, 0] }}
              transition={{ duration: 0.5 }}
            />
            <motion.div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-cyan-400 font-bold text-4xl uppercase tracking-wider"
              initial={{ opacity: 1, scale: 1 }}
              animate={{ opacity: 0, scale: 2 }}
              transition={{ duration: 0.5 }}
            >
              {UI_TEXT.hud.abilityReady}
            </motion.div>
          </>
        )}
      </div>

      {isMobile && (
        <MobileControls
          onDirectionChange={handleDirectionChange}
          onAbilityPress={triggerAbility}
          abilityCooldown={abilityCooldown}
          isAbilityActive={isAbilityActive}
        />
      )}

      <DraftModal
        isOpen={showDraftModal}
        draftText={currentDraftText}
        onClose={() => setShowDraftModal(false)}
        isMobile={isMobile}
      />

      <DraftNotification
        isVisible={isShowingNotification}
        draftText={currentDraftText}
      />

      <AnimatePresence>
        {showPortalHint && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed ${isMobile ? 'top-28' : 'top-32'} left-0 right-0 mx-auto w-max z-50 pointer-events-none`}
          >
            <div
              className={`bg-indigo-950/90 text-white ${isMobile ? 'px-3 py-1.5' : 'px-6 py-3'} border border-indigo-500/50 shadow-[0_0_30px_rgba(99,102,241,0.4)] backdrop-blur-md font-mono rounded-lg`}
            >
              <div className={`${isMobile ? 'text-xs' : 'text-lg'} font-bold text-center text-indigo-200`}>
                {UI_TEXT.portalHint.message}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const LevelEffects = ({ level }) => {
  switch (level) {
    case 1:
      return (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(40)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-white/10 rounded-full"
              initial={{ x: Math.random() * 100 + "%", y: -10 }}
              animate={{
                y: "110vh",
                x: ["0%", (Math.random() * 20 - 10) + "px"]
              }}
              transition={{
                y: {
                  duration: 10 + Math.random() * 10,
                  repeat: Infinity,
                  ease: 'linear',
                  delay: Math.random() * 10
                },
                x: {
                  duration: 3,
                  repeat: Infinity,
                  repeatType: "mirror",
                  ease: "easeInOut"
                }
              }}
            />
          ))}
        </div>
      );
    default:
      return null;
  }
};

export default GameScreen;